/** One in-flight read per resource. Disposal and hiding invalidate its answer. */
export function poll<T>(options: {
  load: (signal: AbortSignal) => Promise<T>;
  accept: (value: T) => void;
  reject: (error: unknown) => void;
  visible: () => boolean;
  subscribe: (wake: () => void) => () => void;
  interval: number;
  again?: (value: T) => boolean;
}) {
  let disposed = false;
  let running = false;
  let finished = false;
  let controller: AbortController | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  async function read() {
    if (disposed || running || finished || !options.visible()) return;
    running = true;
    const current = new AbortController();
    controller = current;
    try {
      const value = await options.load(current.signal);
      if (!disposed && !current.signal.aborted) {
        options.accept(value);
        finished = options.again?.(value) === false;
      }
    } catch (error) {
      if (!disposed && !current.signal.aborted) options.reject(error);
    } finally {
      running = false;
      if (!disposed && !finished && options.visible()) {
        timer = setTimeout(() => void read(), current.signal.aborted ? 0 : options.interval);
      }
    }
  }
  const unsubscribe = options.subscribe(() => {
    clearTimeout(timer);
    if (!options.visible()) controller?.abort();
    else void read();
  });
  void read();
  return () => {
    disposed = true;
    clearTimeout(timer);
    controller?.abort();
    unsubscribe();
  };
}
